import axios from 'axios'
import { Video, Participant, SubtitleInfo, Container } from '../types'

// Attach auth token to every request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('forum_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// On 401, clear token and redirect to login
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname !== '/login') {
      localStorage.removeItem('forum_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  },
)

export const login = (email: string, password: string): Promise<{ token: string }> =>
  axios.post<{ token: string }>('/api/v1/auth/login', { email, password }).then(res => res.data)

// Public (unauthenticated) video endpoints
export interface PublicVideoSummary {
  id: string
  name: string
  description: string
  created_at: string
  has_thumbnail: boolean
}

export const listPublicVideos = () =>
  axios.get<PublicVideoSummary[]>('/api/v1/public/videos').then(res => res.data)

export const getPublicVideo = (id: string) =>
  axios.get<Video>(`/api/v1/public/videos/${id}`).then(res => res.data)

export const extractErrorMessage = (error: any): string => {
  if(error.response && error.response.data) {
    if (error.response.data.message || error.response.data.error) {
      return (error.response.data.message || error.response.data.error) as string
    }
    if (error.response.data) return error.response.data as string
    return error.toString()
  }
  else if(error.error) {
    return error.error
  }
  else if(error.message) {
    return error.message
  }
  else {
    return JSON.stringify(error)
  }
}

export const listVideos = () =>
  axios.get<Video[]>('/api/v1/videos').then(res => res.data)

export const getVideo = (id: string) =>
  axios.get<Video>(`/api/v1/videos/${id}`).then(res => res.data)

export const createVideo = (name: string, description: string) =>
  axios.post<Video>('/api/v1/videos', { name, description }).then(res => res.data)

export const updateVideo = (
  id: string,
  name: string,
  description: string,
  participants: Participant[],
  rotationAdjustment?: number,
  streamUrl?: string,
  language?: string,
  ordering?: number,
  isPrivate?: boolean,
  // Appended rather than grouped with name/description: every argument here is
  // positional and a string, so inserting one in the middle would rebind the
  // existing call sites silently.
  series?: string,
  episode?: string,
) =>
  axios.put<Video>(`/api/v1/videos/${id}`, {
    name,
    description,
    participants,
    rotation_adjustment: rotationAdjustment,
    stream_url: streamUrl,
    language,
    ordering,
    private: isPrivate,
    series,
    episode,
  }).then(res => res.data)

// The all-views layout's framing (stored as four_view); null puts the
// player's defaults back.
export const setFourView = (id: string, framing: { paneAspect: number; zoom: number } | null) =>
  axios.put<Video>(
    `/api/v1/videos/${id}/four-view`,
    framing ? { pane_aspect: framing.paneAspect, zoom: framing.zoom } : { reset: true },
  ).then(res => res.data)

// Where the camera points up or down when the video opens (0 = horizon,
// negative looks down).
export const setStartTilt = (id: string, degrees: number) =>
  axios.put<Video>(`/api/v1/videos/${id}/start-tilt`, { degrees }).then(res => res.data)

export const deleteVideo = (id: string) =>
  axios.delete(`/api/v1/videos/${id}`)

export const updateParticipants = (videoId: string, participants: Participant[]): Promise<Video> =>
  axios.put<Video>(`/api/v1/videos/${videoId}/participants`, { participants }).then(res => res.data)

export const uploadAvatar = (videoId: string, pid: string, file: File): Promise<Video> =>
  axios.post<Video>(`/api/v1/videos/${videoId}/participants/${pid}/avatar`, file, {
    headers: { 'Content-Type': file.type || 'image/jpeg' },
  }).then(res => res.data)

export const uploadThumbnail = (videoId: string, file: File): Promise<Video> =>
  axios.post<Video>(`/api/v1/videos/${videoId}/thumbnail`, file, {
    headers: { 'Content-Type': file.type || 'image/jpeg' },
  }).then(res => res.data)

export const getAvatarUrl = (videoId: string, pid: string): Promise<string> =>
  axios.get<{ url: string }>(`/api/v1/videos/${videoId}/participants/${pid}/avatar`).then(res => res.data.url)

export const uploadSubtitles = (videoId: string, file: File, lang = 'en'): Promise<Video> =>
  axios.post<Video>(`/api/v1/videos/${videoId}/subtitles?lang=${lang}`, file, {
    headers: { 'Content-Type': 'text/plain' },
  }).then(res => res.data)

export const getSubtitles = (videoId: string): Promise<{ subtitles: SubtitleInfo | null }> =>
  axios.get<{ subtitles: SubtitleInfo | null }>(`/api/v1/videos/${videoId}/subtitles`).then(res => res.data)

export const deleteSubtitles = (videoId: string): Promise<void> =>
  axios.delete(`/api/v1/videos/${videoId}/subtitles`)

export const updateSpeakerAttributions = (videoId: string, attributions: Record<string, string>, subtitleOffset: number = 0): Promise<Video> =>
  axios.put<Video>(`/api/v1/videos/${videoId}/attributions`, { speaker_attributions: attributions, subtitle_offset: subtitleOffset }).then(res => res.data)

export const updateAttributionEdits = (
  videoId: string,
  speakerLabels: Record<string, string>,
  attributionOverrides: Record<string, string>,
): Promise<Video> =>
  axios.put<Video>(`/api/v1/videos/${videoId}/attribution-edits`, {
    speaker_labels: speakerLabels,
    attribution_overrides: attributionOverrides,
  }).then(res => res.data)

// Diarization (speaker attribution via STT)
export const startDiarization = (id: string) =>
  axios.post<Video>(`/api/v1/videos/${id}/diarize`).then(res => res.data)

// Reset to a clean slate: removes subtitles, voice groups, labels and overrides.
export const clearDiarization = (id: string) =>
  axios.post<Video>(`/api/v1/videos/${id}/clear-diarization`).then(res => res.data)

export interface DiarizationStatus {
  state: string
  error?: string
  percent?: number
  stage?: string
  speed?: string
  logs?: string[]
}

export const getDiarizationStatus = (id: string) =>
  axios.get<DiarizationStatus>(`/api/v1/videos/${id}/diarize-status?nolog`).then(res => res.data)

// Job history logs
export const getJobLogs = (videoId: string, jobId: string): Promise<string> =>
  axios.get(`/api/v1/videos/${videoId}/jobs/${jobId}/logs`, {
    responseType: 'text',
    transformResponse: [(data: any) => data],
  }).then(res => res.data)

export const updateCueText = (videoId: string, index: number, text: string, lang?: string): Promise<Video> =>
  axios.put<Video>(`/api/v1/videos/${videoId}/subtitles/cues/${index}${lang ? `?lang=${lang}` : ''}`, { text }).then(res => res.data)

// Two people at once (api/pkg/data/overlap.go). Split cuts a master line
// before its word-th word (0-based) into two lines at the same time; join
// folds a line back into the one before it. Both touch every language.
export const splitCue = (videoId: string, index: number, word: number): Promise<Video> =>
  axios.post<Video>(`/api/v1/videos/${videoId}/subtitles/cues/${index}/split`, { word }).then(res => res.data)

export const joinCue = (videoId: string, index: number): Promise<Video> =>
  axios.post<Video>(`/api/v1/videos/${videoId}/subtitles/cues/${index}/join`).then(res => res.data)

// Translation (per-language subtitles via Anthropic)
export interface Language {
  code: string
  name: string
}

export interface TranslationStatus {
  state: string
  error?: string
  percent?: number
  stage?: string
  logs?: string[]
}

export const getLanguages = (): Promise<Language[]> =>
  axios.get<Language[]>(`/api/v1/languages`).then(res => res.data)

export const startTranslation = (id: string, targetLangs: string[]): Promise<Video> =>
  axios.post<Video>(`/api/v1/videos/${id}/translate`, { target_langs: targetLangs }).then(res => res.data)

// Re-label the master subtitle track as another language (e.g. diarization
// saved a German transcript under the default "en").
export const setMasterLanguage = (id: string, language: string): Promise<Video> =>
  axios.put<Video>(`/api/v1/videos/${id}/subtitles/master-language`, { language }).then(res => res.data)

export const getTranslationStatus = (id: string): Promise<TranslationStatus> =>
  axios.get<TranslationStatus>(`/api/v1/videos/${id}/translate-status?nolog`).then(res => res.data)

// Containers — a published video's identity over time (see types.Container).
// ContainerSummary mirrors api/pkg/server.ContainerSummary, which is NOT
// generated into types.ts: it lives in the server package, not api/pkg/types,
// so tygo never sees it. Keep this shape in step with that struct by hand.
export interface ContainerSummary {
  id: string
  slug: string
  ordering?: number
  private?: boolean
  version_count: number
  has_draft: boolean
  video_id: string
  name: string
  description: string
  state: string
  has_thumbnail: boolean
  created_at: string
}

export const listContainers = (): Promise<ContainerSummary[]> =>
  axios.get<ContainerSummary[]>('/api/v1/containers').then(res => res.data)

export const getContainer = (id: string): Promise<Container> =>
  axios.get<Container>(`/api/v1/containers/${id}`).then(res => res.data)

export const updateContainer = (
  id: string,
  slug?: string,
  ordering?: number,
  isPrivate?: boolean,
): Promise<Container> =>
  axios.put<Container>(`/api/v1/containers/${id}`, {
    slug,
    ordering,
    private: isPrivate,
  }).then(res => res.data)

export const discardDraft = (containerId: string): Promise<Container> =>
  axios.delete<Container>(`/api/v1/containers/${containerId}/draft`).then(res => res.data)

// NewVersionResponse mirrors server.NewVersionResponse: the fresh draft and
// the container it now belongs to (its DraftVideoID set to the draft's ID).
export interface NewVersionResponse {
  video: Video
  container: Container
}

export const createNewVersion = (sourceVideoId: string): Promise<NewVersionResponse> =>
  axios.post<NewVersionResponse>(`/api/v1/videos/${sourceVideoId}/new-version`).then(res => res.data)

// ContainerResolution mirrors server.PublicContainerResponse. Used from the
// admin app too, to find the container that owns an arbitrary video — any
// version's video ID resolves, per data.VideoStore.ResolveContainer, and a
// private container still resolves here (Private only hides a container from
// the /public/videos LISTING, never from a direct lookup by key).
export interface ContainerResolution {
  container: Container
  resolved_kind: string
  canonical_slug: string
  video: Video
}

// The public resolve, used by the public and embed pages. `key` is whatever
// the URL carried — a slug, a stale slug, a container id or any version's
// video id — and `versionIndex` selects one version rather than the latest.
// Errors are left to the caller: a 404 here is a real "not found" for a page
// whose whole job is to render that key.
export const resolvePublicContainer = (key: string, versionIndex?: number): Promise<ContainerResolution> =>
  axios.get<ContainerResolution>(`/api/v1/public/containers/${encodeURIComponent(key)}`, {
    params: versionIndex === undefined ? undefined : { v: versionIndex },
  }).then(res => res.data)

// Returns null when the video has no container — which should not normally
// happen, since CreateVideo gives every video one at creation and the boot
// migration backfills the rest. Kept as null rather than an error so a page
// renders "nothing to show yet" instead of failing outright if it ever does.
export const resolveContainerForVideo = (videoId: string): Promise<ContainerResolution | null> =>
  resolvePublicContainer(videoId)
    .catch((err) => {
      if (err.response?.status === 404) return null
      throw err
    })
